
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Link,
  CircularProgress,
  Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Conference } from '../../types/conference';
import { Order } from '../../types/order';
import { APIHOST } from '../../common';
import { ConferenceHotel } from '../../types/conferenceHotel';
import { OrderRoomOccupant } from '../../types/orderRoomOccupant';
import { formatDateFromYYYYMMDD } from '../../../lib/datetime';

interface OrderSummaryProps {
  order: Order;
  setOrderProp: (field: string, value: any) => void;
}

export default function OrderSummary({order, setOrderProp }: OrderSummaryProps) {
  const [selectedHotel, setSelectedHotel] = useState<ConferenceHotel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (order.conference_hotel) {
      fetchHotelDetails();
    }
  }, [order.conference_hotel]);

  const fetchHotelDetails = async () => {
    if (!order.conference_hotel) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(
        `${APIHOST}/api/conference-hotels/${order.conference_hotel.documentId}?populate=*`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch hotel details');
      }
      
      const data = await response.json();
      setSelectedHotel(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while loading hotel details');
      console.error('Error fetching hotel details:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading order summary...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      
      {/* Conference Information Section */}
      {order.conference_group && <Box py={3}>
        
            
            
            <Typography variant="h6" fontWeight="600">
              {order.conference_group.conference.longName}
            </Typography>
        </Box>
      }


      {/* Contact Information Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon color="success" sx={{ ml: 1 }} />
            Contact
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 1 }}>
            {(order.contactFirstName || order.contactLastName) && (
              <Typography variant="body2">
                <strong>Name:</strong> {order.contactFirstName} {order.contactLastName}
              </Typography>
            )}
            
            {order.contactEmail && (
              <Typography variant="body2">
                <strong>Email:</strong> {order.contactEmail}
              </Typography>
            )}
            
            {order.contactCell && (
              <Typography variant="body2">
                <strong>Phone:</strong> {order.contactCell}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Billing Information Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon color="success" sx={{ ml: 1 }} />
            Billing
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 1 }}>
            {order.billingAddressee && (
              <Typography variant="body2">
                <strong>Billing Name:</strong> {order.billingAddressee}
              </Typography>
            )}
            
            {order.billingStreet1 && (
              <Typography variant="body2">
                <strong>Address:</strong> {order.billingStreet1}
                {order.billingStreet2 && `, ${order.billingStreet2}`}
              </Typography>
            )}
            
            {(order.billingCity || order.billingCountry) && (
              <Typography variant="body2">
                <strong>City/Country:</strong> {order.billingCity}
                {order.billingCountry && `, ${order.billingCountry}`}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Transportation Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon color="success" sx={{ ml: 1 }} />
            Transportation
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 1 }}>
            
            {order.requiresBusParking && (
              <Typography variant="body2">
                <strong>Bus Parking:</strong> Required
              </Typography>
            )}
            
            {order.requiresTransitToVenue && (
              <Typography variant="body2">
                <strong>Transit to Venue:</strong> Required
              </Typography>
            )}
            
            {!order.requiresBusParking && !order.requiresTransitToVenue && (
              <Typography variant="body2" color="text.secondary">
                No transportation requirements specified
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Room & Occupants Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon color="success" sx={{ ml: 1 }} />
            Rooms & Occupants
          </Typography>
         
          <Box sx={{ display: 'grid', gap: 2 }}>
            {order.order_rooms && order.order_rooms.length > 0 ? (
              <>
                <Typography variant="body2">
                  <strong>Total Rooms:</strong> {order.order_rooms.length}
                </Typography>
                {order.order_rooms.map((room, index) => (
                  <Box key={index} sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', ml: 1 }}>
                    <Typography variant="body2" fontWeight="600">
                      Room {index + 1}
                    </Typography>
                    
                    {room.type && (
                      <Typography variant="body2">
                        <strong>Type:</strong> {room.type}
                      </Typography>
                    )}
                    
                    {room.arrivalDate && (
                      <Typography variant="body2">
                        <strong>Check-in Date:</strong> {formatDateFromYYYYMMDD(room.arrivalDate)}
                      </Typography>
                    )}
                    
                    {room.departureDate && (
                      <Typography variant="body2">
                        <strong>Check-out Date:</strong> {formatDateFromYYYYMMDD(room.departureDate)}
                      </Typography>
                    )}

                    {room.order_room_occupants && room.order_room_occupants.length > 0 && (
                      <>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Occupants ({room.order_room_occupants.length}):</strong>
                        </Typography>
                        {room.order_room_occupants.map((occupant: OrderRoomOccupant, occupantIndex: number) => (
                          <Typography key={occupantIndex} variant="body2" sx={{ ml: 2 }}>
                            • {occupant.firstName} {occupant.lastName}
                         
                          </Typography>
                        ))}
                      </>
                    )}
                    
                    
                  </Box>
                ))}
                {order.notesForHotel && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>Special Requests:</strong> {order.notesForHotel}
                      </Typography>
                    )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No rooms selected
              </Typography>
            )}
            
          
            
            {order.notesForHotel && (
              <Typography variant="body2">
                <strong>General Notes:</strong> {order.notesForHotel}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Hotel Information Section */}
      {selectedHotel && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon color="success" sx={{ ml: 1 }} />
              Hotel
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight="600">
                {selectedHotel.hotel.longName}
              </Typography>
              
              {(selectedHotel.hotel.addressCity || selectedHotel.hotel.addressState || selectedHotel.hotel.addressZip) && (
                <Typography variant="body2" color="text.secondary">
                  {selectedHotel.hotel.addressCity}
                  {selectedHotel.hotel.addressState && `, ${selectedHotel.hotel.addressState}`}
                  {selectedHotel.hotel.addressZip && ` ${selectedHotel.hotel.addressZip}`}
                </Typography>
              )}
              
              {selectedHotel.hotel.website && (
                <Link href={selectedHotel.hotel.website} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.875rem' }}>
                  Visit Hotel Website
                </Link>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {order.rewardsNumber && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>Rewards/Loyalty Number:</strong> {order.rewardsNumber}
                </Typography>
              </Box>
            )}

            {order.termsAccepted && (
              <Box sx={{ mb: 1 }}>
                <Chip 
                  label="Terms & Conditions Accepted" 
                  color="success" 
                  size="small" 
                  icon={<CheckCircleIcon />}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

    </Box>
  );
}